import React, { useState, useEffect, useRef } from 'react';
import styles from './AutoCompleteInput.module.css';
import {X} from "lucide-react";

export interface AutoCompleteInputProps<T> {
    label: string;
    value: T | null;
    onChange: (value: T | null) => void;
    searchFn: (term: string) => Promise<T[]>;
    renderOption: (item: T) => React.ReactNode;
    displayValue: (item: T | null) => string;
    placeholder?: string;
    required?: boolean;
}

export function AutoCompleteInput<T>({
                                         label,
                                         value,
                                         onChange,
                                         searchFn,
                                         renderOption,
                                         displayValue,
                                         placeholder,
                                         required = false,
                                     }: AutoCompleteInputProps<T>) {
    const [searchText, setSearchText] = useState('');
    const [options, setOptions] = useState<T[]>([]);
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [selectedValue, setSelectedValue] = useState<T | null>(value);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        setSelectedValue(value);
        setSearchText(value ? displayValue(value) : '');
    }, [value, displayValue]);

    // Debounce + recherche
    useEffect(() => {
        if (!isFocused) return;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(async () => {
            if (!searchText) {
                setOptions([]);
                return;
            }
            setLoading(true);
            try {
                const results = await searchFn(searchText);
                setOptions(results);
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [searchText, searchFn, isFocused]);

    const handleInputChange = (text: string) => {
        setSearchText(text);
        // on ne touche pas selectedValue tant qu'aucune option n'est choisie
    };

    const handleSelect = (item: T) => {
        setSelectedValue(item);
        setSearchText(displayValue(item));
        setIsFocused(false);
        setOptions([]);
        onChange(item);
    };

    const handleReset = () => {
        setSelectedValue(null);
        setSearchText('');
        onChange(null);
        setOptions([]);
    };

    return (
        <div className={styles.autocompleteWrapper}>
            <label className={styles.label}>
                {label} {required && '*'}
            </label>
            <div className={styles.inputWrapper}>
                <div className={styles.inputWrapper}>
                    <input
                        type="text"
                        className={styles.input}
                        placeholder={placeholder}
                        value={searchText}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onFocus={() => setIsFocused(true)}
                    />

                    {selectedValue && (
                        <button
                            type="button"
                            className={styles.clearBtn}
                            onClick={handleReset}
                            aria-label="Effacer la sélection"
                        >
                            <X size={16} strokeWidth={2} />
                        </button>
                    )}
                </div>
            </div>

            {isFocused && (
                <div className={styles.dropdownContainer}>
                    {loading && (
                        <div className={styles.loading}>Recherche...</div>
                    )}

                    {!loading && options.length === 0 && searchText && (
                        <div className={styles.noResult}>
                            Aucun résultat
                        </div>
                    )}

                    {!loading && options.length > 0 && (
                        <ul className={styles.dropdown}>
                            {options.map((item, idx) => (
                                <li
                                    key={idx}
                                    onMouseDown={() => handleSelect(item)} // évite blur avant click
                                    className={styles.option}
                                >
                                    <div className={styles.optionContent}>
                                        {renderOption(item)}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {loading && <div className={styles.loading}>Chargement...</div>}
        </div>
    );
}